import {Injectable} from '@angular/core';
import {Headers, Http, RequestOptions, Response, ResponseContentType, URLSearchParams} from '@angular/http';
import {ActivatedRoute, Router} from '@angular/router';
import {Observable, Subject} from 'rxjs';

import {SessionService} from './session.service';
import {AuthService} from './auth.service';
import {NotificationService} from './notification.service';
import {UtilService} from './util.service';
import {AlertService} from './alert.service';

import {SmartLoaderService} from './smart-loader.service';

import {saveAs} from 'file-saver';

import 'rxjs/Rx';

@Injectable()
export class RequestService {

    private startRequest = new Subject<{ url: string, id: string }>();
    private completeRequest = new Subject<{ url: string, id: string }>();

    private requestStarted: Observable<any>;
    private requestCompleted: Observable<any>;
    private currentRoute: Observable<string>;

    constructor(
        private http: Http,
        private router: Router,
        private route: ActivatedRoute,
        private sessionService: SessionService,
        private authService: AuthService,
        private notificationService: NotificationService,
        private loadingService: SmartLoaderService,
        private utilService: UtilService,
        private alertService: AlertService
    ) { }

    public get(url: string, query?: any, responseType?: any, suppressError: boolean = false) {
        let headers: any = {
            'Content-Type': 'application/json'
        }

        if (this.sessionService.getToken()) {
            headers['Authorization'] = 'Bearer ' + this.sessionService.getToken()
        }

        let options = {
            headers: headers
        };
        if (query) {
            let search = new URLSearchParams();

            query = this.utilService.trim(query);

            Object.keys(query).forEach(function (key) {
                search.set(key, query[key]);
            });
            options['search'] = search;
        }
        if (responseType) {

            switch (responseType) {
                case 'text':
                    options['responseType'] = ResponseContentType.Text;
                    break;
                case 'json':
                    options['responseType'] = ResponseContentType.Json;
                    break;
                case 'blob':
                    options['responseType'] = ResponseContentType.Blob;
                    break;
                default:
                    throw new Error(responseType + ' not a valid option');
            }


        }
        let requestOptions = new RequestOptions(options);

        return Observable.defer(() => {
            let id = this.loadingService.beginRequest(url);

            return this.http.get(url, requestOptions)
                .map(this.extractData.bind(this))
                .finally(() => this.loadingService.endRequest(id, url))
                .catch(err => this.handleError(err, suppressError));
        });
    }

    public getDelegate(url: string, query?: any, responseType?: any) {
        let headers: any = {
            'Content-Type': 'application/json'
        }

        if (this.sessionService.getToken()) {
            headers['Authorization'] = 'Bearer ' + this.sessionService.getToken()
        }

        let options = {
            headers: headers
        };
        if (query) {
            let search = new URLSearchParams();

            query = this.utilService.trim(query);

            Object.keys(query).forEach(function (key) {
                search.set(key, query[key]);
            });
            options['search'] = search;
        }
        if (responseType) {

            switch (responseType) {
                case 'text':
                    options['responseType'] = ResponseContentType.Text;
                    break;
                case 'json':
                    options['responseType'] = ResponseContentType.Json;
                    break;
                case 'blob':
                    options['responseType'] = ResponseContentType.Blob;
                    break;
                default:
                    throw new Error(responseType + ' not a valid option');
            }


        }
        let requestOptions = new RequestOptions(options);

        return Observable.defer(() => {
            let id = this.loadingService.beginRequest(url);

            return this.http.get(url, requestOptions)
                .map(this.extractData.bind(this))
                .finally(() => this.loadingService.endRequest(id, url))
                .catch(this.handleErrorDelegate.bind(this));
        });
    }

    public post(url: string, body: any, suppressError: boolean = false, params?: URLSearchParams, additionalHeaders?: any) {
        body = this.utilService.trim(body);
        let headers = new Headers({
            'Content-Type': 'application/json'
        });

        if (this.sessionService.getToken()) {
            headers.append('Authorization', 'Bearer ' + this.sessionService.getToken());
        }

        if (additionalHeaders) {
            Object.keys(additionalHeaders).forEach(_ => {
                headers.append(_, additionalHeaders[_]);
            })
        }

        let requestOptions = new RequestOptions();
        requestOptions.headers = headers;
        if (params) {
            requestOptions.params = params;
        }

        let id = this.loadingService.beginRequest(url);

        return Observable.defer(() => {
            return this.http.post(url, body, requestOptions)
                .map(this.extractData.bind(this))
                .finally(() => this.loadingService.endRequest(id, url))
                .catch(err => this.handleError(err, suppressError));
        });
    }

    private extractData(res: Response) {
        let body;
        if (res.headers.get('content-disposition')) {
            this.handleFileResponse(res);
        } else {
            // We need to handle case when backend returns 200 with empty body
            try {
                body = res.json();
            } catch (e) {
                body = "";
            }
        }
        return body;
    }

    private handleFileResponse(res: Response) {

        let content = res.blob();
        let filename = res.headers.get('content-disposition')
            .split('; filename=\"')[1]
            .slice(0, -1);

        let type = res.headers.get('content-type');

        let blob = new Blob([content], { type: type });
        saveAs(blob, filename);
        // var url= window.URL.createObjectURL(blob);
        // window.open(url);
    }

    private handleError(err: any, suppressError: boolean = false) {
        switch (err.status) {
            case 401: return this.forceLogout(err, true);
            case 403: return this.returnError(err, '', suppressError);
            default: return this.returnError(err, null, suppressError);
        }
    }

    private handleErrorDelegate(err: any, supressError) {
        switch (err.status) {
            case 401: return this.forceLogout(err, supressError);
            case 403: return this.returnErrorDelegate(err, 'Forbidden');
            default: return this.returnErrorDelegate(err);
        }
    }

    private forceLogout(err, suppressError) {
        this.authService.login();
        return this.returnError(err, null, suppressError);
    }

    private formatErrorMessage(resp) {

        let sessionExpired = resp.sessionExpired;

        resp = resp.json();
        let error = resp.error || resp;
        let message = (error.message) ? error.message : 'Server error';

        if (sessionExpired) message = 'Session Expired';

        return message;
    }

    private returnError(err, title?: string, suppress: boolean = false) {
        if (!suppress) {
            this.notificationService.error(this.formatErrorMessage(err), title || 'Failed');
        }

        return Observable.throw(err);
    }

    private returnErrorDelegate(err, title?: string) {
        return Observable.throw(err);
    }
}



// WEBPACK FOOTER //
// ./src/app/core/services/request.service.ts